import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isIndexableSite } from '../seo/indexing';
import { absoluteUrl, DEFAULT_OG_IMAGE, type SeoMeta } from '../seo/seoConfig';

type SeoHeadProps = {
  meta: SeoMeta;
  jsonLd?: object | object[];
  hreflang?: string;
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string) {
  const selector = `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(data: object | object[]) {
  const id = 'cassette-seo-jsonld';
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SeoHead({ meta, jsonLd, hreflang = 'en' }: SeoHeadProps) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = hreflang === 'jp' ? 'ja' : hreflang || i18n.language || 'en';
    document.documentElement.lang = lang;

    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);

    if (meta.noindex || !isIndexableSite()) {
      upsertMeta('name', 'robots', 'noindex, nofollow');
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      robots?.remove();
    }

    const canonical = absoluteUrl(meta.path);
    upsertLink('canonical', canonical);

    const ogImage = meta.ogImage ?? DEFAULT_OG_IMAGE;
    const ogType = meta.ogType ?? 'website';

    const ogDescription = meta.ogDescription ?? meta.description;

    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', ogDescription);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:site_name', 'Cassette');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', ogDescription);
    upsertMeta('name', 'twitter:image', ogImage);

    if (import.meta.env.VITE_GSC_VERIFICATION) {
      upsertMeta('name', 'google-site-verification', import.meta.env.VITE_GSC_VERIFICATION);
    }

    const xDefaultLink = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (!xDefaultLink) {
      const alt = document.createElement('link');
      alt.rel = 'alternate';
      alt.hreflang = 'x-default';
      alt.href = canonical;
      document.head.appendChild(alt);
    } else {
      (xDefaultLink as HTMLLinkElement).href = canonical;
    }

    if (jsonLd) {
      upsertJsonLd(jsonLd);
    } else {
      document.getElementById('cassette-seo-jsonld')?.remove();
    }
  }, [meta, jsonLd, hreflang, i18n.language]);

  return null;
}
